import zipCodeToTimezone from 'zipcode-to-timezone';
import dayjs, { extend } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';

// Extend dayjs with timezone support
extend(utc);
extend(timezone);
extend(advancedFormat);

/**
 * Get timezone from US zip code
 * @param zipCode - US zip code (e.g., "94102", "10001")
 * @returns IANA timezone string (e.g., "America/Los_Angeles") or null if not found
 */
export const getTimezoneFromZipCode = (zipCode: string): string | null => {
  if (!zipCode || typeof zipCode !== 'string') {
    return null;
  }

  // Clean zip code (remove spaces, dashes)
  const cleanZipCode = zipCode.trim().split('-')[0];

  try {
    const tz = zipCodeToTimezone.lookup(cleanZipCode);
    return tz || null;
  } catch (error) {
    console.warn(`Failed to lookup timezone for zip code: ${zipCode}`, error);
    return null;
  }
};

/**
 * Get current local time for a given timezone
 * @param timezone - IANA timezone string (e.g., "America/Los_Angeles")
 * @param format - dayjs format string (default: "h:mm A" - "3:45 PM")
 * @returns Formatted time string or null if timezone is invalid
 */
export const getLocalTime = (timezone: string | null, format = 'h:mm A'): string | null => {
  if (!timezone) {
    return null;
  }

  try {
    return dayjs().tz(timezone).format(format);
  } catch (error) {
    console.warn(`Failed to get local time for timezone: ${timezone}`, error);
    return null;
  }
};

/**
 * Get current local time from zip code
 * @param zipCode - US zip code
 * @param format - dayjs format string (default: "h:mm A")
 * @returns Formatted time string or null if zip code is invalid
 */
export const getLocalTimeFromZipCode = (zipCode: string, format = 'h:mm A'): string | null => {
  const tz = getTimezoneFromZipCode(zipCode);
  return getLocalTime(tz, format);
};

/**
 * Get timezone abbreviation (e.g., "PST", "EST")
 * @param timezone - IANA timezone string
 * @returns Timezone abbreviation or null
 */
export const getTimezoneAbbreviation = (timezone: string | null): string | null => {
  if (!timezone) {
    return null;
  }

  try {
    // Use zzz for full timezone abbreviation (PST, PDT, EST, etc.)
    return dayjs().tz(timezone).format('zzz');
  } catch (error) {
    console.warn(`Failed to get timezone abbreviation for: ${timezone}`, error);
    return null;
  }
};

/**
 * Get full local time info from zip code
 * @param zipCode - US zip code
 * @returns Object with time, timezone, and abbreviation or null
 */
export const getLocalTimeInfo = (zipCode: string) => {
  const tz = getTimezoneFromZipCode(zipCode);

  if (!tz) {
    return null;
  }

  const time = getLocalTime(tz);
  const abbreviation = getTimezoneAbbreviation(tz);

  return {
    time,
    timezone: tz,
    abbreviation,
  };
};
